import React, { useEffect, useRef } from 'react'
import { Box, Flex, Grid, Icon, Text } from '@chilipiper/design-system'
import { AlertBanner, Button, Spinner } from '@chilipiper/design-system/src/new'
import { SvgSandClock } from '@chilipiper/icons'
import riveWasmUrl from '@rive-app/webgl2/rive.wasm?url'
import {
  RuntimeLoader,
  useRive,
  Layout,
  Fit,
  Alignment,
  useViewModelInstanceColor,
  useViewModelInstanceBoolean,
} from '@rive-app/react-webgl2'
import { parseColorToRgb } from '@chilipiper/utils'
import { useMessageFeedContext } from '../chat-widget/MessageFeedContext'
import { useChatScroll } from '../hooks/useChatScroll'
import { useMarkLatestVisibleEvent } from '..'
import { useConversationRoomLoader } from '../chat-widget/useConversationRoomLoader'
import { useScrollFetch } from '../hooks/useScrollFetch'
import { TranslationProvider, useTranslations } from '../context/TranslationContext'
import { Header } from './header/Header'
import { StyledOverflowFlex } from './styles'
import { FeedBlock } from './blocks/FeedBlock'
import { TypingAnimation } from './TypingAnimation'
import { ReconnectingStatus } from './header/ReconnectingStatus'
import { ChatInput } from './ChatInput'
import { ConsentFooter } from './ConsentFooter'
import { useAppearanceSettings } from './ChiliTheme'
import animationBinary from './glow_border.riv'

interface Props {
  onChatClose?: () => void
  onSessionRestart: (options?: { force: boolean }) => void
  isRestarting?: boolean
}

RuntimeLoader.setWasmUrl(riveWasmUrl)

const RECONNECTING_STATES = ['RECONNECTING', 'CONNECTED', 'ERROR']

export const Chat = ({ onChatClose, onSessionRestart, isRestarting }: Props) => {
  const {
    blocks,
    channel,
    isTyping,
    reconnectingStatus,
    sendMessage,
    sendingMessage,
    liveChatActive,
    isLoading,
    guestId,
    roomId,
    isNarrowScreen,
    tenantId,
    workspaceId,
    piperAiActive,
    isWaitingForPiperAiResponse,
    isSessionExpired,
  } = useMessageFeedContext()

  const {
    isLoading: isConversationsLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversationRoomLoader(roomId ?? '', true, !!roomId)
  const scrollRef = useRef<HTMLDivElement>(null)

  const { handleLastVisibleEvent } = useMarkLatestVisibleEvent(`${guestId}_guest`, blocks)

  const { ref: feedBodyRef, isScrolledBeyondFirstMessage } = useChatScroll(blocks.length)

  const appearanceSettings = useAppearanceSettings()

  useScrollFetch({
    scrollRef,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
  })

  const { RiveComponent, rive } = useRive({
    src: animationBinary,
    stateMachines: 'thinking',
    autoplay: true,
    layout: new Layout({
      fit: Fit.Fill,
      alignment: Alignment.Center,
    }),
    autoBind: true,
  })

  const color = useViewModelInstanceColor('color', rive?.viewModelInstance)
  const isThinking = useViewModelInstanceBoolean('is_thinking', rive?.viewModelInstance)

  useEffect(() => {
    const actionColorRgb = parseColorToRgb(appearanceSettings.actionColor)
    color.setRgb(actionColorRgb.r, actionColorRgb.g, actionColorRgb.b)
  }, [appearanceSettings.actionColor, color])

  useEffect(() => {
    isThinking.setValue(isWaitingForPiperAiResponse)
  }, [isThinking, isWaitingForPiperAiResponse])

  if (!blocks || !channel) {
    return null
  }
  const lastBlock = blocks.at(-1)
  const isReconnecting = RECONNECTING_STATES.includes(reconnectingStatus)
  const isInitiated = blocks.length > 2
  const isLoadingInitialMessages = isLoading || (isConversationsLoading && !isInitiated)
  const isLastBlockFillFormSubmitted =
    lastBlock?.type === 'FillForm' && !!Object.keys(lastBlock.values ?? {}).length

  const rootBorderRadius = isNarrowScreen ? 0 : 20
  return (
    <TranslationProvider tenantId={tenantId} workspaceId={workspaceId ?? ''}>
      <Grid
        data-test-id='Chat-wrapper'
        boxShadow={isNarrowScreen ? undefined : 'border/section'}
        bg='bg/section'
        w='100%'
        h='100%'
        borderRadius={rootBorderRadius}
        gridTemplateRows='minmax(0, auto)'
        gridTemplateColumns='minmax(0, auto)'
      >
        <RiveComponent
          style={{
            gridArea: '1 / 1',
            borderRadius: rootBorderRadius,
            overflow: 'hidden',
          }}
        />
        <Flex
          flexDirection='column'
          gridArea='1 / 1'
          zIndex={
            1 /* not needed in other browsers, but mobile safari needs it (otherwise RiveComponent will cover it and block all clicks) */
          }
        >
          <Box flexShrink={0}>
            <Header
              channel={channel}
              onClose={onChatClose}
              onRestart={() => onSessionRestart({ force: true })}
              isRestarting={isRestarting}
              isDividerFullWidth={isScrolledBeyondFirstMessage}
              showRestart={isInitiated && !liveChatActive}
            />
            {isReconnecting && <ReconnectingStatus reconnectingStatus={reconnectingStatus} />}
          </Box>
          <StyledOverflowFlex ref={scrollRef} flexDirection='column' flexGrow={1} w='100%'>
            {isConversationsLoading && !isLoadingInitialMessages && (
              <Flex mt={2} alignSelf='center'>
                <Spinner />
              </Flex>
            )}
            {!!isError && (
              <Flex>
                <RefetchingErrorAlertBanner onClick={refetch} />
              </Flex>
            )}

            <Box
              display='flex'
              flexDirection='column'
              flexGrow={1}
              w='100%'
              py={6}
              px='section'
              ref={feedBodyRef}
            >
              <Flex flexDirection='column' gap={5} flexGrow={1}>
                {blocks?.map((block, index) => (
                  <FeedBlock
                    key={
                      /**
                       * The first 2 blocks could be initially rendered as an optimistic update
                       * where we don't have access to id yet. We don't want to remount the
                       * component because it would replay the animation
                       */
                      index <= 1 ? index : block.id
                    }
                    block={block}
                    onLiveMessageVisible={handleLastVisibleEvent}
                    isLastBlock={index === blocks.length - 1}
                  />
                ))}
                {(isTyping || isLoadingInitialMessages || isLastBlockFillFormSubmitted) && (
                  <TypingAnimation />
                )}
              </Flex>
            </Box>
          </StyledOverflowFlex>
          <Flex flexDirection='column' flexShrink={0}>
            {isSessionExpired ? (
              <ExpiredSessionBanner
                onSessionRestart={onSessionRestart}
                isRestarting={isRestarting}
              />
            ) : (
              <>
                {(liveChatActive || piperAiActive || isWaitingForPiperAiResponse) && (
                  <Box mx={3}>
                    <ChatInput
                      sendMessage={sendMessage}
                      sendingMessage={sendingMessage}
                      isDisabled={isWaitingForPiperAiResponse}
                    />
                  </Box>
                )}
                <Box pt={2} pb={3} px={5}>
                  <ConsentFooter consentFooter={appearanceSettings?.consentFooter || ''} />
                </Box>
              </>
            )}
          </Flex>
        </Flex>
      </Grid>
    </TranslationProvider>
  )
}

const ExpiredSessionBanner = ({
  onSessionRestart,
  isRestarting,
}: {
  onSessionRestart: () => void
  isRestarting?: boolean
}) => {
  const [sessionExpired, sessionExpiredDescription, startNewChat] = useTranslations([
    'sessionExpired',
    'sessionExpiredDescription',
    'startNewChat',
  ])
  return (
    <Flex flexDirection='column' m={2} p={5} gap={4} bg='bg/section-emphasis' borderRadius={12}>
      <Flex flexDirection='column' gap={3}>
        <Flex gap={2} alignItems='center'>
          <Icon color='fg/minor' icon={SvgSandClock} />
          <Text textStyle='page-section-heading' color='fg/major'>
            {sessionExpired}
          </Text>
        </Flex>
        <Text textStyle='body-multiline' color='fg/moderate'>
          {sessionExpiredDescription}
        </Text>
      </Flex>
      <Button onClick={onSessionRestart} variant='major' isFullWidth isLoading={isRestarting}>
        {startNewChat ?? 'Start New Chat'}
      </Button>
    </Flex>
  )
}

const RefetchingErrorAlertBanner = ({ onClick }: { onClick: () => void }) => (
  <Box flexGrow={1} overflow='hidden' borderRadius={12} mx={6} mt={3}>
    <AlertBanner
      actions={[
        {
          onClick,
          text: 'Refetch',
          variant: 'minor',
        },
      ]}
      type='inline'
      title='Error occurred while loading messages.'
      variant='caution'
    />
  </Box>
)
